import { action, computed, makeObservable, observable, runInAction, toJS } from 'mobx';
import { JobFormManager } from '../job-form.manager';
import { SpecialityViewModel } from 'jobs/models/specialty.view-model';
import { v4 as uuidv4 } from 'uuid';
import { JobCategory } from 'jobs/types';
import { JobApiService } from 'jobs/JobApiService';

type CategoryControl = {
   id: string;
   category: string;
   subCategory: SpecialityViewModel;
};

export class JobCategoriesGroupStore {
   readonly categoriesGroup = observable.array([
      {
         id: uuidv4(),
         category: '',
         subCategory: new SpecialityViewModel({
            onValueChange: (values) => {
               this.jobManager.form.controls.subCategory.setValue(values.map((cat) => cat.id));
            },
         }),
      },
   ]);

   constructor(public readonly jobManager: JobFormManager) {
      makeObservable(this, {
         onCategoryChange: action.bound,
         categories: computed,
         categoryControls: computed,
         subCategoryControls: computed,
         onAddControl: action.bound,
         onRemoveControl: action.bound,
      });
   }

   get categoryControls() {
      return [
         this.jobManager.form.controls.category,
         this.jobManager.form.controls.category_1,
         this.jobManager.form.controls.category_2,
      ];
   }

   get subCategoryControls() {
      return [
         this.jobManager.form.controls.subCategory,
         this.jobManager.form.controls.subCategory_1,
         this.jobManager.form.controls.subCategory_2,
      ];
   }

   async onCategoryChange(idx: number, category: string): Promise<void> {
      const catControl = this.categoriesGroup[idx];
      if (catControl) {
         catControl.category = category;

         if (idx === 0) {
            this.jobManager.form.controls.category.setValue(catControl.category);
         }

         if (idx === 1) {
            this.jobManager.form.controls.category_1.setValue(catControl.category);
         }

         if (idx === 2) {
            this.jobManager.form.controls.category_2.setValue(catControl.category);
         }

         await this._getSubCategories(catControl.subCategory, category);
      }
   }

   onSubCategoryChange(idx: number, values: JobCategory[]): void {
      this.subCategoryControls[idx].setValue(values.map((cat) => cat.id));
   }

   onAddControl(): void {
      let control = this.jobManager.form.controls.subCategory_1;
      if (this.categoriesGroup.length === 2) {
         control = this.jobManager.form.controls.subCategory_2;
      }
      if (this.categoriesGroup.length < 3) {
         this.categoriesGroup.push({
            id: uuidv4(),
            category: '',
            subCategory: new SpecialityViewModel({
               onValueChange: (values) => {
                  control.setValue(toJS(values).map((cat) => cat.id));
               },
            }),
         });
      }
   }

   onRemoveControl(ctrl: CategoryControl): void {
      this.categoriesGroup.remove(ctrl);
   }

   get categories(): JobCategory[] {
      const jobAttr = this.jobManager.jobAttributes.get();
      if (jobAttr) {
         return jobAttr.categories;
      }
      return [];
   }

   private _getSubCategories = async (
      subCaategoryVM: SpecialityViewModel,
      category: string
   ): Promise<void> => {
      try {
         const resp = await JobApiService.getSubCategories(Number(category));
         if (resp.status === 200) {
            runInAction(() => {
               subCaategoryVM.set(resp.response);
            });
         }
      } catch (e) {
         runInAction(() => {
            console.error('JobCategories: ' + e);
         });
      }
   };
}
